import React, { useEffect, useRef } from 'react';
import { colorPalette } from '@sharefiledev/antd-config';
import { Flex, Typography } from 'antd';
import { t } from '../../../../utils';
import { ClearSignatureButton } from '../ClearSignatureButton';
import { SignatureAreaDimensions, SignatureState, SignatureTab } from '../constants';
import ESignatureCanvas from '../ESignatureCanvas';
import { SaveSignatureEvent } from '../SaveSignatureEvent';

const { Paragraph } = Typography;

type ESignaturePadProps = {
	penColor: string;
	saveSignature: (url: string, data: string, text: string) => void;
	savedSignatureJsonData: string;
	isSignatureSigned: SignatureState;
	setIsSignatureSigned: (value: SignatureState) => void;
};

export const ESignaturePad = ({
	penColor,
	saveSignature,
	savedSignatureJsonData,
	isSignatureSigned,
	setIsSignatureSigned,
}: ESignaturePadProps) => {
	const canvasRef = useRef(null);

	const clearSignature = () => {
		canvasRef.current.clear();
		setIsSignatureSigned(SignatureState.NOT_SIGNED);
	};

	const onSignEnd = () => {
		setIsSignatureSigned(SignatureState.SIGNED);
	};

	useEffect(() => {
		const canvasRefValue = canvasRef.current;
		if (
			canvasRefValue &&
			savedSignatureJsonData &&
			isSignatureSigned === SignatureState.SAVED_SIGNATURE
		) {
			canvasRefValue.fromData(JSON.parse(savedSignatureJsonData));
		}

		const onSaveEventHandler: EventListener = (event: Event) => {
			if ((event as CustomEvent).detail.signatureType !== SignatureTab.DRAWN) {
				return;
			}
			if (canvasRefValue) {
				const signatureDataUrl = canvasRefValue.toDataURL('image/png');
				const data = canvasRefValue.toData();
				saveSignature(signatureDataUrl, JSON.stringify(data), null);
			}
		};

		SaveSignatureEvent.registerHandler(onSaveEventHandler);
		return () => {
			SaveSignatureEvent.unRegisterHandler(onSaveEventHandler);
		};
	}, [savedSignatureJsonData, saveSignature, isSignatureSigned]);

	const style = {
		border: `1px solid ${colorPalette.neutral7}`,
		height: SignatureAreaDimensions.height, // container height
	};

	return (
		<Flex vertical style={{ position: 'relative' }} data-testid="signature-pad">
			<Paragraph>{t('signatureAnnotationMessage')}</Paragraph>
			<Flex vertical justify="center" style={style}>
				<ESignatureCanvas
					ref={canvasRef}
					canvasProps={{ ...SignatureAreaDimensions }}
					onEnd={onSignEnd}
					data-testid="signature-canvas-pad"
					penColor={penColor}
					minWidth={2}
				/>
			</Flex>
			{isSignatureSigned !== SignatureState.NOT_SIGNED && (
				<ClearSignatureButton
					onClick={clearSignature}
					data-testid="signature-draw-clear-link"
				/>
			)}
		</Flex>
	);
};
